/**
 * worldSessionFactory.ts
 * Shared factory for the homogeneous per-world platformer session helpers.
 *
 * Worlds 3, 4, and 5 share an identical session surface — resolve a
 * `?level=` id, build a `{ id, tileMap }` bundle, init the PlayScene with
 * the same 12-argument `scene.init` call, walk a linear next-level chain,
 * and persist completion with the same race-guarded read-modify-write.
 * Before this factory each of those bodies was copy-pasted across three
 * files, so a fix to (e.g.) the save race guard had to land in three
 * places. This module is the single source of truth; each
 * `levelNSession.ts` becomes a small config block plus re-exports.
 *
 * DECISION: World 1 is deliberately NOT built on this factory. It carries
 * genuinely world-specific wiring the trio lacks — a finale cutscene
 * (portal gate + boarding ramp on 1-1 and 1-3), a `canSkip` init
 * parameter, and a `finaleConfig` on its session bundle. Folding that
 * into the factory would contort the shared shape to serve one outlier,
 * so `level1Session.ts` stays bespoke. The dispatcher
 * (`platformerSessionBundle.ts`) already treats W1 as the special case
 * (4-arg `initWorld1Scene`) while the trio use the common 3-arg form.
 *
 * @module worldSessionFactory
 */

import { getLevel } from './LevelRegistry';
import type { TileMapResult } from './TileMapLoader';
import type { WorldId } from '../../constants/gameConstants';
import type { PlayScene } from '../scenes/PlayScene';
import { gameState } from '../engine/gameState';
import SaveManager from '../services/saveManager';
import Logger from '../utils/logger';
import { mergeIds } from './mergeIds';

/**
 * Common session bundle shape shared by Worlds 3, 4, and 5. The per-world
 * `WorldNSession` types are aliases of this interface.
 */
export interface WorldSession {
  /** Save-key identifier (e.g. `'4-1'`, `'4-B'`, `'4-0'`). */
  readonly id: string;
  /** Underlying parsed tile-map result. */
  readonly tileMap: TileMapResult;
}

/** Optional ability unlock fired when a specific boss-arena id completes. */
export interface WorldAbilityUnlock {
  /** Canonical level id whose completion triggers the unlock (e.g. `'4-B'`). */
  readonly bossId: string;
  /** Idempotent unlock persister (e.g. `persistDashUnlock`). */
  readonly persist: () => Promise<void>;
}

/** Declarative description of one homogeneous platformer world (W3/W4/W5). */
export interface WorldSessionConfig {
  /** Numeric world id passed to {@link getLevel}. */
  readonly worldId: WorldId;
  /** Human label used in thrown-error messages, e.g. `'World 4'`. */
  readonly worldLabel: string;
  /**
   * Canonical id → LevelRegistry integer level number. Secret stages map
   * to `0`, boss arenas to `5`, matching the LevelRegistry convention.
   */
  readonly idToLevelNumber: Readonly<Record<string, number>>;
  /** Fallback id for unknown/missing router input, e.g. `'4-1'`. */
  readonly fallbackId: string;
  /**
   * Normal-progression chain: current id → next id. Terminal stages
   * (boss arena, secret stage) are simply absent and resolve to `null`.
   */
  readonly nextChain: Readonly<Record<string, string>>;
  /** Optional ability unlock fired on a specific boss-arena clear. */
  readonly abilityUnlock?: WorldAbilityUnlock;
}

/** The five per-world helpers produced by {@link createWorldSession}. */
export interface WorldSessionHelpers {
  /** Resolve a `?level=` value to a canonical id, or the fallback id. */
  resolveLevelId(raw: string | string[] | undefined): string;
  /** Build a session bundle, throwing on an unregistered id. */
  getSession(levelId: string): WorldSession;
  /** Initialise the PlayScene from a session bundle (no finale wiring). */
  initScene(scene: PlayScene, session: WorldSession, skipCheckpointRestore?: boolean): void;
  /** Next normal-progression id, or `null` if there is none. */
  getNextLevelId(levelId: string): string | null;
  /** Persist completion stats for the level. Fire-and-forget. */
  persistCompletion(levelId: string): Promise<void>;
}

/**
 * Build the shared session-helper bundle for one homogeneous world.
 *
 * @param config - Declarative per-world description.
 * @returns The five session helpers, each closing over `config`.
 */
export function createWorldSession(config: WorldSessionConfig): WorldSessionHelpers {
  const { worldId, worldLabel, idToLevelNumber, fallbackId, nextChain, abilityUnlock } = config;

  function resolveLevelId(raw: string | string[] | undefined): string {
    const candidate = Array.isArray(raw) ? raw[0] : raw;
    // DECISION: Object.prototype.hasOwnProperty.call so prototype keys
    // like 'toString' or '__proto__' don't sneak through the `in` operator.
    if (
      candidate !== undefined &&
      Object.prototype.hasOwnProperty.call(idToLevelNumber, candidate)
    ) {
      return candidate;
    }
    return fallbackId;
  }

  function getSession(levelId: string): WorldSession {
    // DECISION: Own-property + typeof check — guards against prototype
    // keys ('toString', '__proto__') sneaking past plain-object indexing.
    if (!Object.prototype.hasOwnProperty.call(idToLevelNumber, levelId)) {
      throw new Error(`Unknown ${worldLabel} level id: ${levelId}`);
    }
    const num = idToLevelNumber[levelId];
    if (typeof num !== 'number') {
      throw new Error(`Unknown ${worldLabel} level id: ${levelId}`);
    }
    const tileMap = getLevel(worldId, num);
    if (tileMap === undefined) {
      throw new Error(`${worldLabel} level ${levelId} not registered`);
    }
    return { id: levelId, tileMap };
  }

  function initScene(
    scene: PlayScene,
    session: WorldSession,
    skipCheckpointRestore = false,
  ): void {
    const { tileMap } = session;
    scene.init(
      tileMap.levelRuntime,
      tileMap.levelData.playerSpawnX,
      tileMap.levelData.playerSpawnY,
      session.id,
      tileMap.checkpoints,
      tileMap.levelData.starSeeds,
      tileMap.levelData.constellationShards,
      tileMap.levelData.heartFragments,
      tileMap.enemySpawns,
      undefined,
      skipCheckpointRestore,
      tileMap.levelData.weaponPickups,
    );
  }

  function getNextLevelId(levelId: string): string | null {
    return Object.prototype.hasOwnProperty.call(nextChain, levelId)
      ? nextChain[levelId]
      : null;
  }

  async function persistCompletion(levelId: string): Promise<void> {
    // DECISION: Snapshot gameState BEFORE any await — otherwise a fast
    // Next-tap (or the boss-clear → world-map transition resetting
    // PlayScene) can wipe gameState.collectibles/score between the await
    // and the read. Any ability unlock is awaited AFTER the snapshot for
    // the same reason; the unlock persisters are idempotent so the
    // ordering is safe on retry.
    const collectedSeeds = gameState.collectibles
      .filter((c) => c.collected && c.kind === 'seed')
      .map((c) => c.id);
    const collectedShards = gameState.collectibles
      .filter((c) => c.collected && c.kind === 'shard')
      .map((c) => c.id);
    const collectedHearts = gameState.collectibles
      .filter((c) => c.collected && c.kind === 'heart_fragment')
      .map((c) => c.id);
    const scoreSnapshot = gameState.score;
    if (abilityUnlock !== undefined && levelId === abilityUnlock.bossId) {
      await abilityUnlock.persist();
    }
    try {
      const existing = (await SaveManager.load()) ?? SaveManager.createDefaultSave();
      const prev = existing.levels[levelId];
      existing.levels[levelId] = {
        completed: true,
        collectedStarSeedIds: mergeIds(prev?.collectedStarSeedIds, collectedSeeds),
        collectedConstellationShardIds: mergeIds(
          prev?.collectedConstellationShardIds,
          collectedShards,
        ),
        collectedHeartFragmentIds: mergeIds(prev?.collectedHeartFragmentIds, collectedHearts),
        lastCheckpointId: null,
      };
      existing.totalScore = existing.totalScore + scoreSnapshot;
      await SaveManager.save(existing);
    } catch (err) {
      Logger.error('GameScreen', 'Failed to save level progress', err);
    }
  }

  return { resolveLevelId, getSession, initScene, getNextLevelId, persistCompletion };
}
